import axios from "../config/axios";
import { apiUrl } from "../native/nativeApiOrigin";
import { platformRef } from "../platform/platformRef";
import { RevisionSheetDelta } from "./revisionDelta";
import { projectScheduleApi } from "./projectCoreAxios";
import type { ProjectActiveSchedule, ProjectScheduleProjectsResponse } from "./projectCore";

import {
    ApiActiveSchedule,
    ApiConnection,
    ApiExternalSearchResult,
    ApiSectionGroup,
    ApiElementsResponse,
    ApiDocumentSummary,
    ApiFile,
    ApiFragmentNode,
    ApiSearchResult,
    ApiScheduleActivity,
    ApiScheduleProjectsResponse,
    ApiScheduleRevision,
    ApiTextBox,
    ApiWBSTreeResponse,
    ApiWBSChildrenResponse,
    ApiActivityResolveResponse,
    TableRowSection,
} from "../shared/interfaces";
import { FileWithPath } from "react-dropzone";
import { FileUploader, UploadProgressCallback } from "../util/FileUploader";
import { openAuthenticatedSseResponse } from "../util/authenticatedFetch";
import { getStreamErrorMessage, parseSseFrame } from "../util/streamUtils";

// Current schedule wire projects omit the UI-only `isLoaded` decoration.
// Preserve the established FileApi declarations without changing wire objects.
function asLegacyScheduleProjects(
    projects: Promise<ProjectScheduleProjectsResponse>,
): Promise<ApiScheduleProjectsResponse> {
    return projects as unknown as Promise<ApiScheduleProjectsResponse>;
}

function asLegacyActiveSchedule(
    active: Promise<{ schedule: ProjectActiveSchedule | null }>,
): Promise<{ schedule: ApiActiveSchedule | null }> {
    return active as unknown as Promise<{ schedule: ApiActiveSchedule | null }>;
}

interface SearchStreamCallbacks {
    onResult: (result: ApiSearchResult) => void;
    onExplanation?: (id: string, explanation: string | null, score: number | null) => void;
    /** Synced-but-not-imported files matching the query (Document Management v2). */
    onExternalResult?: (result: ApiExternalSearchResult) => void;
    onDone: (count: number) => void;
    onError: (error: string) => void;
}

interface FileElementsOptions {
    includeUnconnected?: boolean;
    revisionId?: string;
    /** Scope to the viewed section group — deduped sheet files are shared by
     *  many groups across packages/revisions; unscoped responses stack every
     *  copy's components and clicks can navigate to another package. */
    sectionGroupId?: string;
    /** Component mode: also return inactive detector-seeded components, which
     *  only activate on the sheet's first save or segmentation approval. */
    includeDetectorSeeds?: boolean;
}

/** Where one schedule activity lives (GET /schedule/activity/{section uuid}). */
export interface ApiScheduleActivityLocation {
    fileId: string;
    taskCode: string;
    projectId: string | null;
}

export interface SheetBootstrapParams {
    fileId: string;
    page: number;
    includeUnconnected: boolean;
    /** Connection/element projection. Omit for the document's active projection. */
    revisionId?: string;
    /** Asks for this sheet's revision delta (with `revisionId`, the server's pairing). */
    sectionGroupId?: string;
    /** Elements scoping (shop dedupe) — separate so contract delta requests stay unscoped. */
    scopeSectionGroupId?: string;
}

export interface ApiSheetBootstrap {
    elements: ApiElementsResponse;
    // Same shape (and same missing-value caveat) as getPageDimensions.
    dimensions: { width: number; height: number };
    sheet_delta: RevisionSheetDelta | null;
}

function handleSearchResultData(data: string, onResult: SearchStreamCallbacks["onResult"]) {
    try {
        onResult(JSON.parse(data) as ApiSearchResult);
    } catch (e) {
        console.error("Failed to parse search result:", e);
    }
}

function handleSearchExplanationData(
    data: string,
    onExplanation: SearchStreamCallbacks["onExplanation"],
) {
    if (!onExplanation) {
        return;
    }

    try {
        const parsed = JSON.parse(data) as {
            id: string;
            explanation?: string | null;
            score?: number | null;
        };
        onExplanation(parsed.id, parsed.explanation ?? null, parsed.score ?? null);
    } catch (e) {
        console.error("Failed to parse explanation:", e);
    }
}

function handleSearchExternalResultData(
    data: string,
    onExternalResult: SearchStreamCallbacks["onExternalResult"],
) {
    if (!onExternalResult) {
        return;
    }

    try {
        onExternalResult(JSON.parse(data) as ApiExternalSearchResult);
    } catch (e) {
        console.error("Failed to parse external search result:", e);
    }
}

function handleSearchDoneData(data: string, onDone: SearchStreamCallbacks["onDone"]) {
    try {
        onDone(JSON.parse(data).count);
    } catch {
        onDone(0);
    }
}

function handleSearchErrorData(data: string, onError: SearchStreamCallbacks["onError"]) {
    onError(getStreamErrorMessage(data, "Search failed"));
}

function processSearchStreamEvent(
    event: { event: string; data: string },
    callbacks: SearchStreamCallbacks,
): boolean {
    switch (event.event) {
        case "result":
            handleSearchResultData(event.data, callbacks.onResult);
            return false;
        case "explanation":
            handleSearchExplanationData(event.data, callbacks.onExplanation);
            return false;
        case "external_result":
            handleSearchExternalResultData(event.data, callbacks.onExternalResult);
            return false;
        case "done":
            handleSearchDoneData(event.data, callbacks.onDone);
            return true;
        case "error":
            handleSearchErrorData(event.data, callbacks.onError);
            return true;
        default:
            return false;
    }
}

/** Returns whether the stream delivered its terminal `done` or `error` frame. */
async function consumeSearchStream(
    body: ReadableStream<Uint8Array>,
    callbacks: SearchStreamCallbacks,
): Promise<boolean> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) {
                break;
            }

            buffer += decoder.decode(value, { stream: true }).replace(/\r/g, "");

            let boundaryIndex = buffer.indexOf("\n\n");
            while (boundaryIndex !== -1) {
                const rawEvent = buffer.slice(0, boundaryIndex);
                buffer = buffer.slice(boundaryIndex + 2);

                if (rawEvent.trim()) {
                    const shouldStop = processSearchStreamEvent(parseSseFrame(rawEvent), callbacks);
                    if (shouldStop) {
                        return true;
                    }
                }

                boundaryIndex = buffer.indexOf("\n\n");
            }
        }

        buffer += decoder.decode().replace(/\r/g, "");
        return buffer.trim() ? processSearchStreamEvent(parseSseFrame(buffer), callbacks) : false;
    } finally {
        reader.releaseLock();
    }
}

/**
 * Search always streams over fetch, never `EventSource`: a browser `EventSource`
 * cannot send the Access client-protocol headers, and ai_engine answers a
 * protected or enforced project's streams without them with 426. Like the
 * `EventSource` it replaces, it does not reconnect, and a stream that ends
 * without its `done`/`error` frame reports "Search connection failed".
 */
function createSearchStream(params: URLSearchParams, callbacks: SearchStreamCallbacks) {
    const url = apiUrl(`/api/ai/search?${params.toString()}`);
    const abortController = new AbortController();
    let isClosed = false;

    const close = () => {
        isClosed = true;
        abortController.abort();
    };

    void (async () => {
        try {
            const { response, usedEmbedAuth } = await openAuthenticatedSseResponse(url, {
                signal: abortController.signal,
                withCredentials: platformRef.current.auth.withCredentials,
            });

            if (isClosed) {
                return;
            }

            if (!response.ok) {
                if (usedEmbedAuth && response.status === 401) {
                    return;
                }
                const fallback =
                    response.status === 401 ? "Cannot validate session" : "Search failed";
                callbacks.onError(getStreamErrorMessage(await response.text(), fallback));
                close();
                return;
            }

            if (!response.body) {
                throw new Error("SSE response body is missing");
            }

            if (!(await consumeSearchStream(response.body, callbacks)) && !isClosed) {
                throw new Error("Search stream ended before its final event");
            }
            close();
        } catch (error) {
            if (isClosed || abortController.signal.aborted) {
                return;
            }

            console.error("Search stream failed:", error);
            callbacks.onError("Search connection failed");
            close();
        }
    })();

    return close;
}

export const FileApi = {
    getFile: async (id: string, signal?: AbortSignal): Promise<ApiFile> => {
        const resp = await axios.get(`/api/ai/file/${id}`, { signal });
        return resp.data;
    },

    getPageDimensions: async (fileId: string): Promise<{ width: number; height: number }> => {
        const resp = await axios.get<{ width: number; height: number }>(
            `/api/ai/file/${fileId}/dimensions`,
        );
        return resp.data;
    },

    /** Single text box by UUID — e.g. a sheet's `title_textbox` anchor. */
    getTextbox: async (textboxId: string): Promise<ApiTextBox> => {
        const resp = await axios.get<ApiTextBox>(`/api/ai/textbox/${textboxId}`);
        return resp.data;
    },

    getFileSectionGroups: async (id: string): Promise<ApiSectionGroup[]> => {
        const resp = await axios.get(`/api/ai/file/${id}/section_groups`);
        return resp.data;
    },

    searchSheets: async (
        documentId: string,
        query: string,
        areaId?: string,
    ): Promise<ApiSectionGroup[]> => {
        const resp = await axios.get(`/api/ai/file/${documentId}/section_groups`, {
            params: { search: query, area_id: areaId },
        });
        return resp.data;
    },

    getFragmentNode: async (id: string): Promise<ApiFragmentNode> => {
        const resp = await axios.get(`/api/ai/fragment_node/${id}`);
        return resp.data;
    },

    getFileElements: async (
        id: string,
        page: number,
        options: FileElementsOptions = {},
    ): Promise<ApiElementsResponse> => {
        const {
            includeUnconnected = false,
            revisionId,
            sectionGroupId,
            includeDetectorSeeds,
        } = options;
        const resp = await axios.get(`/api/ai/file/${id}/elements`, {
            params: {
                page,
                includeUnconnected,
                revision_id: revisionId,
                section_group_id: sectionGroupId,
                include_detector_seeds: includeDetectorSeeds || undefined,
            },
        });
        return resp.data;
    },

    /** One-call sheet-open payload (elements + dimensions + optional revision delta). The viewer
     *  and the overlay read it through `useSheetBootstrap`, one react-query entry per key. */
    getSheetBootstrap: async (params: SheetBootstrapParams): Promise<ApiSheetBootstrap> => {
        const resp = await axios.get<ApiSheetBootstrap>(
            `/api/ai/file/${params.fileId}/sheet_bootstrap`,
            {
                params: {
                    page: params.page,
                    includeUnconnected: params.includeUnconnected,
                    revision_id: params.revisionId,
                    section_group_id: params.sectionGroupId,
                    elements_scope_section_group_id: params.scopeSectionGroupId,
                },
            },
        );
        return resp.data;
    },

    getAllFiles: async (): Promise<ApiFile[]> => {
        const resp = await axios.get("/api/ai/files");
        return resp.data;
    },

    /**
     * Get document counts summary.
     * Replacement for getAllFiles in most cases - provides counts without loading full file data.
     */
    getDocumentSummary: async (): Promise<ApiDocumentSummary> => {
        const resp = await axios.get("/api/ai/documents/summary");
        return resp.data;
    },

    getUploadedFiles: async (): Promise<ApiFile[]> => {
        const resp = await axios.get("/api/ai/files/uploads");
        return resp.data;
    },

    setFileType: async (id: string, fileType: string): Promise<ApiFile> => {
        const resp = await axios.put(`/api/ai/file/${id}/file_type`, {
            value: fileType,
        });
        return resp.data;
    },

    uploadFile: async (
        file: FileWithPath & { fileType?: string },
        onProgress?: UploadProgressCallback,
        marvinV2: boolean = false,
    ): Promise<ApiFile> => {
        return FileUploader.uploadFile(file, onProgress, marvinV2);
    },

    /** Related leaves, already thresholded and bounded by the server. `summary`
     *  rows carry only what a result row renders; `includeDrawings: false`
     *  skips drawing results (and their ranking) for a caller that hides them. */
    getRelatedFragmentNodes: async (
        id: string,
        fragmentUuid: string,
        options: { view?: "full" | "summary"; includeDrawings?: boolean } = {},
    ): Promise<ApiFragmentNode[]> => {
        const params: Record<string, string | boolean> = { fragmentUuid };
        if (options.view) params.view = options.view;
        if (options.includeDrawings === false) params.include_drawings = false;
        const resp = await axios.get(`/api/ai/file/${id}/related_fragment_nodes`, { params });
        return resp.data;
    },

    /**
     * Stream search results using Server-Sent Events.
     * Phase 1: Results delivered immediately in relevance order
     * Phase 2: Explanations streamed as LLM generates them
     */
    searchDocumentStream: (options: {
        documentId: string;
        query: string;
        limit?: number;
        onResult: (result: ApiSearchResult) => void;
        onExplanation?: (id: string, explanation: string | null, score: number | null) => void;
        onDone: (count: number) => void;
        onError: (error: string) => void;
    }): (() => void) => {
        const params = new URLSearchParams({
            query: options.query,
            document_id: options.documentId,
            limit: (options.limit ?? 20).toString(),
        });
        return createSearchStream(params, options);
    },

    /**
     * Stream search results across ALL project documents using Server-Sent Events.
     * `scope` narrows the search (e.g. "contract-docs", "category:documents",
     * "folder:<uuid>") — see Document Management v2 scoped search.
     */
    searchProjectStream: (options: {
        query: string;
        limit?: number;
        scope?: string;
        onResult: (result: ApiSearchResult) => void;
        onExplanation?: (id: string, explanation: string | null, score: number | null) => void;
        onExternalResult?: (result: ApiExternalSearchResult) => void;
        onDone: (count: number) => void;
        onError: (error: string) => void;
    }): (() => void) => {
        const params = new URLSearchParams({
            query: options.query,
            limit: (options.limit ?? 100).toString(),
        });
        if (options.scope) {
            params.set("scope", options.scope);
        }
        return createSearchStream(params, options);
    },

    getConnection: async (connectionId: string): Promise<ApiConnection> => {
        const resp = await axios.get(`/api/ai/connection/${connectionId}`);
        return resp.data;
    },

    getDetailReferences: async (sectionUuid: string): Promise<ApiConnection[]> => {
        const resp = await axios.get(`/api/ai/section/${sectionUuid}/references`);
        return resp.data;
    },

    getTableRows: async (sectionUuid: string): Promise<TableRowSection[]> => {
        const resp = await axios.get(`/api/ai/section/${sectionUuid}/children`);
        return resp.data;
    },

    /** The detail chips connection mode paints on a sheet destination: the sheet
     *  root's keyed DrawingDetails in the table-row shape (`keys: [{key, connected}]`).
     *  Twins sharing one printed id carry the same key and state. */
    getSheetDetailKeys: async (sheetSectionUuid: string): Promise<TableRowSection[]> => {
        const resp = await axios.get(`/api/ai/section/${sheetSectionUuid}/detail_keys`);
        return resp.data;
    },

    getPageTables: async (
        fileId: string,
        page: number,
        sectionGroupId?: string,
    ): Promise<ApiFragmentNode[]> => {
        const resp = await axios.get(`/api/ai/file/${fileId}/page_tables`, {
            params: { page, section_group_id: sectionGroupId },
        });
        return resp.data;
    },

    deleteDocument: async (fileId: string): Promise<{ message: string }> => {
        const resp = await axios.delete(`/api/ai/admin/document/${fileId}`);
        return resp.data;
    },

    /** Re-trigger ingestion for a document whose latest workflow failed — the
     *  register table's Retry action on a Failed row. */
    retryDocument: async (documentId: string): Promise<{ message: string }> => {
        const resp = await axios.post(`/api/ai/documents/${documentId}/retry`);
        return resp.data;
    },

    // Sheet Revision Management
    getSheetRevisions: async (
        documentId: string,
        groupCode: string,
        sectionGroupId?: string,
    ): Promise<ApiSectionGroup[]> => {
        // section_group_id scopes the list to that sheet's own area chain;
        // without it the engine merges every area copy of the group code.
        const resp = await axios.get(
            `/api/ai/section_groups/${documentId}/${groupCode}/revisions`,
            {
                params: { section_group_id: sectionGroupId },
            },
        );
        return resp.data;
    },

    uploadSheetRevision: async (
        documentId: string,
        groupCode: string,
        file: File,
        sectionGroupId?: string,
        onUploadProgress?: (progressEvent: any) => void,
    ): Promise<ApiSectionGroup> => {
        const formData = new FormData();
        formData.append("file", file);
        // Disambiguates when several active sheets share the group code (area
        // twins); omitting it makes the engine 409 in that state.
        if (sectionGroupId) formData.append("section_group_id", sectionGroupId);

        const resp = await axios.post(
            `/api/ai/section_groups/${documentId}/${groupCode}/revision`,
            formData,
            {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress,
            },
        );
        return resp.data;
    },

    setRevisionActive: async (
        sectionGroupId: string,
        isActive: boolean,
    ): Promise<ApiSectionGroup> => {
        const resp = await axios.patch(`/api/ai/section_groups/${sectionGroupId}/active`, {
            is_active: isActive,
        });
        return resp.data;
    },

    deleteSheetHistory: async (sectionGroupId: string): Promise<Record<string, unknown>> => {
        const resp = await axios.delete(`/api/ai/section_groups/${sectionGroupId}/history`);
        return resp.data;
    },

    getScheduleActivities: async (fileId: string): Promise<ApiScheduleActivity[]> =>
        projectScheduleApi.getScheduleActivities(fileId),

    /**
     * Search schedule activities by text.
     * Uses backend search to find activities across all WBS nodes.
     */
    searchScheduleActivities: async (
        fileId: string,
        query: string,
        options?: { criticalOnly?: boolean; limit?: number; projectId?: string },
    ): Promise<{ activities: ApiScheduleActivity[]; totalMatches: number; query: string }> =>
        projectScheduleApi.searchScheduleActivities(fileId, query, options),

    /**
     * Get projects in a schedule file (for multi-project XER files).
     * Returns a list of projects with metadata (activity count, date range, etc.)
     */
    getScheduleProjects: async (fileId: string): Promise<ApiScheduleProjectsResponse> =>
        asLegacyScheduleProjects(projectScheduleApi.getScheduleProjects(fileId)),

    /**
     * Get WBS tree structure for a schedule file (lazy loading).
     * Returns top-level WBS nodes with metadata but NOT individual activities.
     */
    getScheduleWBSTree: async (fileId: string, projectId?: string): Promise<ApiWBSTreeResponse> =>
        projectScheduleApi.getScheduleWBSTree(fileId, projectId),

    /**
     * Get children of a WBS node (lazy loading on expand).
     * Returns both child WBS containers and direct child activities.
     */
    getWBSChildren: async (wbsId: string): Promise<ApiWBSChildrenResponse> =>
        projectScheduleApi.getWBSChildren(wbsId),

    /** The schedule file, task code and project of one activity, by its section uuid: what a
     *  `/schedule?activity=<section uuid>` link needs to highlight it. */
    locateScheduleActivity: async (sectionUuid: string): Promise<ApiScheduleActivityLocation> => {
        const resp = await axios.get<ApiScheduleActivityLocation>(
            `/api/ai/schedule/activity/${sectionUuid}`,
        );
        return resp.data;
    },

    /**
     * Resolve a schedule activity by task code and return the full WBS ancestor
     * chain with pre-loaded children for instant tree expansion.
     */
    resolveScheduleActivity: async (
        fileId: string,
        taskCode: string,
        options?: { projectId?: string },
    ): Promise<ApiActivityResolveResponse> =>
        projectScheduleApi.resolveScheduleActivity(fileId, taskCode, options),

    // ========================================================================
    // Schedule Upload & Revisions
    // ========================================================================

    uploadSchedule: async (
        file: File,
    ): Promise<{ fileId: string; jobId: number; message: string }> => {
        const formData = new FormData();
        formData.append("file", file);
        const resp = await axios.post("/api/ai/schedule/upload", formData);
        return resp.data;
    },

    getScheduleUploadStatus: async (
        fileId: string,
        jobId?: number,
    ): Promise<{
        status: string;
        fileId: string;
        title?: string;
        jobStatus?: string;
        error?: string;
    }> => {
        const params = jobId != null ? { job_id: jobId } : undefined;
        const resp = await axios.get(`/api/ai/schedule/upload/status/${fileId}`, { params });
        return resp.data;
    },

    getActiveSchedule: async (): Promise<{ schedule: ApiActiveSchedule | null }> =>
        asLegacyActiveSchedule(projectScheduleApi.getActiveSchedule()),

    getScheduleRevisions: async (): Promise<{ revisions: ApiScheduleRevision[] }> =>
        projectScheduleApi.getScheduleRevisions(),

    activateScheduleRevision: async (fileId: string): Promise<{ message: string }> => {
        const resp = await axios.put(`/api/ai/schedule/revisions/${fileId}/activate`);
        return resp.data;
    },

    deleteScheduleRevision: async (fileId: string): Promise<{ message: string }> => {
        const resp = await axios.delete(`/api/ai/schedule/revisions/${fileId}`);
        return resp.data;
    },

    deleteSheetsBatch: async (
        sectionGroupIds: string[],
    ): Promise<{
        deleted_count: number;
        deleted_ids?: string[];
        failed_ids?: string[];
        not_found_ids?: string[];
        invalid_ids?: string[];
        results?: Array<{
            section_group_id: string;
            success: boolean;
            error?: string;
        }>;
    }> => {
        const resp = await axios.post(`/api/ai/section_groups/batch_delete`, {
            section_group_ids: sectionGroupIds,
        });
        return resp.data;
    },
};
